/**
 * RibbonPreviewModal — Tiled LED Ribbon Viewer (Tiled Preview)
 *
 * Renders grouped tiled assets (1–4 levels) as concentric cylinders
 * viewed from the inside. Supports mp4 video and still images.
 * Used for "LED Tiled" content type (assets with -TL- filename pattern).
 *
 * Props:
 *   asset          – the parent asset record (for title display)
 *   ribbonItems    – array of { name, downloadUrl } for each level file
 *   onClose        – callback to close the modal
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";

// ── Colour constants (self-contained, no theme dependency) ───────────
const TEAL = "#2fd6b5";
const TEXT_SECONDARY = "rgba(255,255,255,0.5)";

// ── Level / image helpers ───────────────────────────────────────────────
const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp", ".svg"];

/**
 * Extract the level tag from a tiled filename.
 * Pattern: -TL-{LEVEL} or _TL_{LEVEL} before the file extension.
 * e.g. "..._MCG-TL-FOP.mp4" → "FOP", "..._SCG-TL-PAR.mp4" → "PAR"
 */
function parseLevel(name) {
  const n = (name || "").toUpperCase();
  // Match -TL- or _TL_ followed by the level tag (letters/digits) before the extension
  const m = n.match(/[-_]TL[-_]([A-Z0-9]+)(?:\.[A-Z0-9]+)?$/);
  return m ? m[1] : null;
}

function isImageFile(name) {
  const lower = (name || "").toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

function toDirectUrl(url) {
  if (!url) return "";
  return url
    .replace(/^_+/, "").replace(/_+$/, "")  // strip __ wrappers from Airtable
    .replace("dl=0", "dl=1")
    .replace("www.dropbox.com", "dl.dropboxusercontent.com");
}

// ── Component ───────────────────────────────────────────────────────────
export default function RibbonPreviewModal({ asset, ribbonItems, onClose }) {
  const containerRef = useRef(null);
  const stateRef = useRef(null);          // holds Three.js objects for cleanup
  const [ready, setReady] = useState(false);
  const [loadingCount, setLoadingCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [allPlaying, setAllPlaying] = useState(false);

  // ── Escape key ────────────────────────────────────────────────────────
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", h);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  // ── Play / stop helpers exposed via ref ───────────────────────────────
  const playAll = useCallback(() => {
    const S = stateRef.current;
    if (!S) return;
    S.getActiveRows().forEach((r) => {
      const row = S.rows[r];
      if (row?.ready && !row.isImage && row.video) {
        row.video.currentTime = 0;
        row.video.play().catch(() => {});
        row.playing = true;
      }
    });
    S.allPlaying = true;
    setAllPlaying(true);
  }, []);

  const stopAll = useCallback(() => {
    const S = stateRef.current;
    if (!S) return;
    S.getActiveRows().forEach((r) => {
      const row = S.rows[r];
      if (row && !row.isImage && row.video) {
        row.video.pause();
        row.playing = false;
      }
    });
    S.allPlaying = false;
    setAllPlaying(false);
  }, []);

  const resetView = useCallback(() => {
    const S = stateRef.current;
    if (!S) return;
    S.rotationY = 0;
    S.pivotGroup.rotation.y = 0;
    S.getActiveRows().forEach((r) => {
      const row = S.rows[r];
      if (row?.ready && !row.isImage && row.video) {
        row.video.currentTime = 0;
        if (row.playing) row.video.play().catch(() => {});
      }
    });
  }, []);

  // ── Timer display ─────────────────────────────────────────────────────
  const timerRef = useRef(null);

  // ── Three.js init (runs once when ribbonItems arrive) ─────────────────
  useEffect(() => {
    if (!containerRef.current || !ribbonItems || ribbonItems.length === 0) return;

    const validItems = ribbonItems.filter((i) => parseLevel(i.name));
    if (validItems.length === 0) return;
    setTotalCount(validItems.length);

    // Dynamic import Three.js from CDN (keeps bundle small)
    let cancelled = false;
    let cleanupFn = null;

    (async () => {
      const THREE = await import(
        /* webpackIgnore: true */
        "https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.js"
      );

      if (cancelled) return;

      // ── Constants ──
      const TILES_PER_ROW = 5;
      const RING_RADIUS = 8;
      const RING_GAP = 0.3;
      const RING_SEGMENTS = 128;
      const DRAG_SENSITIVITY = 0.004;

      // ── State held in ref ──
      const rows = {};
      let rotationY = 0;
      let isDragging = false;
      let lastPointerX = 0;
      let mediaLoaded = 0;

      // Build ordered level list from actual items (top-to-bottom render order)
      const LEVEL_ORDER_HINT = ["L5", "L4", "L3", "L2", "L1", "PAR", "FOP"];
      const discoveredLevels = [...new Set(validItems.map((i) => parseLevel(i.name)).filter(Boolean))];
      discoveredLevels.sort((a, b) => {
        const ai = LEVEL_ORDER_HINT.indexOf(a);
        const bi = LEVEL_ORDER_HINT.indexOf(b);
        // Known levels sort by hint order; unknown levels go to the end alphabetically
        if (ai >= 0 && bi >= 0) return ai - bi;
        if (ai >= 0) return -1;
        if (bi >= 0) return 1;
        return a.localeCompare(b);
      });

      const getActiveRows = () => discoveredLevels.filter((r) => rows[r]?.ring);

      // ── Renderer ──
      const el = containerRef.current;
      const W = el.clientWidth;
      const H = el.clientHeight;

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(W, H);
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      el.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x0a0a12);

      const camera = new THREE.PerspectiveCamera(50, W / H, 0.001, 100);
      camera.position.set(0, 0, 0.1);

      scene.add(new THREE.AmbientLight(0xffffff, 0.3));

      const pivotGroup = new THREE.Group();
      scene.add(pivotGroup);

      // ── Drag handling ──
      const onPointerDown = (e) => {
        isDragging = true;
        lastPointerX = e.clientX;
        renderer.domElement.style.cursor = "grabbing";
      };
      const onPointerMove = (e) => {
        if (!isDragging) return;
        const dx = e.clientX - lastPointerX;
        lastPointerX = e.clientX;
        rotationY += dx * DRAG_SENSITIVITY;
        pivotGroup.rotation.y = rotationY;
      };
      const onPointerUp = () => {
        isDragging = false;
        renderer.domElement.style.cursor = "grab";
      };

      renderer.domElement.style.cursor = "grab";
      renderer.domElement.addEventListener("pointerdown", onPointerDown);
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);

      // Touch
      const onTouchStart = (e) => {
        if (e.touches.length === 1) { isDragging = true; lastPointerX = e.touches[0].clientX; }
      };
      const onTouchMove = (e) => {
        if (!isDragging || e.touches.length !== 1) return;
        const dx = e.touches[0].clientX - lastPointerX;
        lastPointerX = e.touches[0].clientX;
        rotationY += dx * DRAG_SENSITIVITY;
        pivotGroup.rotation.y = rotationY;
      };
      const onTouchEnd = () => { isDragging = false; };

      renderer.domElement.addEventListener("touchstart", onTouchStart, { passive: true });
      renderer.domElement.addEventListener("touchmove", onTouchMove, { passive: true });
      renderer.domElement.addEventListener("touchend", onTouchEnd);

      // ── Build ring helper ──
      function buildRing(level, w, h) {
        const circumference = 2 * Math.PI * RING_RADIUS;
        const tileArcWidth = circumference / TILES_PER_ROW;
        const ringHeight = tileArcWidth / (w / h);
        rows[level].ringHeight = ringHeight;

        const geo = new THREE.CylinderGeometry(RING_RADIUS, RING_RADIUS, ringHeight, RING_SEGMENTS, 1, true);
        const mat = new THREE.MeshBasicMaterial({ map: rows[level].texture, side: THREE.BackSide, toneMapped: false });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.rotation.y = Math.PI;
        pivotGroup.add(mesh);
        rows[level].ring = mesh;
      }

      function repositionRings() {
        const active = getActiveRows();
        let total = 0;
        active.forEach((r) => { total += rows[r].ringHeight; });
        total += (active.length - 1) * RING_GAP;
        let y = total / 2;
        active.forEach((r) => {
          const h = rows[r].ringHeight;
          y -= h / 2;
          rows[r].ring.position.y = y;
          rows[r].yPos = y;
          y -= h / 2 + RING_GAP;
        });
      }

      function onAllLoaded() {
        repositionRings();
        setReady(true);
      }

      // ── Load each level ──
      validItems.forEach((item) => {
        const level = parseLevel(item.name);
        if (!level) return;
        const isImage = isImageFile(item.name);
        const directUrl = toDirectUrl(item.downloadUrl);

        if (isImage) {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.src = directUrl;

          const texture = new THREE.Texture(img);
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.generateMipmaps = false;
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.ClampToEdgeWrapping;
          texture.repeat.set(-TILES_PER_ROW, 1);
          texture.offset.x = 1;

          rows[level] = { video: null, image: img, texture, ring: null, playing: false, ready: false, yPos: 0, ringHeight: 0, isImage: true };

          img.onload = () => {
            if (rows[level].ready) return;
            rows[level].ready = true;
            texture.needsUpdate = true;
            mediaLoaded++;
            setLoadingCount(mediaLoaded);
            buildRing(level, img.naturalWidth, img.naturalHeight);
            if (mediaLoaded === validItems.length) onAllLoaded();
          };
          img.onerror = () => {
            console.error(`[Ribbon] ${level} image load error`);
            mediaLoaded++;
            setLoadingCount(mediaLoaded);
            if (mediaLoaded === validItems.length) onAllLoaded();
          };
        } else {
          const video = document.createElement("video");
          video.crossOrigin = "anonymous";
          video.loop = true;
          video.muted = true;
          video.playsInline = true;
          video.preload = "metadata";
          video.src = directUrl;

          const texture = new THREE.VideoTexture(video);
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.generateMipmaps = false;
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.ClampToEdgeWrapping;
          texture.repeat.set(-TILES_PER_ROW, 1);
          texture.offset.x = 1;

          rows[level] = { video, image: null, texture, ring: null, playing: false, ready: false, yPos: 0, ringHeight: 0, isImage: false };

          video.addEventListener("loadedmetadata", () => {
            if (rows[level].ready) return;
            rows[level].ready = true;
            mediaLoaded++;
            setLoadingCount(mediaLoaded);
            buildRing(level, video.videoWidth, video.videoHeight);
            if (mediaLoaded === validItems.length) onAllLoaded();
          });
          video.addEventListener("error", () => {
            console.error(`[Ribbon] ${level} video load error`);
            mediaLoaded++;
            setLoadingCount(mediaLoaded);
            if (mediaLoaded === validItems.length) onAllLoaded();
          });
        }
      });

      // ── Timer update ──
      function fmt(s) {
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec.toString().padStart(2, "0")}`;
      }

      function updateTimer() {
        // Use FOP first, then fall back to first video row found
        const fopRow = rows["FOP"];
        let v = fopRow && fopRow.ready && !fopRow.isImage ? fopRow.video : null;
        if (!v) {
          for (const lvl of discoveredLevels) {
            const row = rows[lvl];
            if (row && row.ready && !row.isImage && row.video) { v = row.video; break; }
          }
        }
        if (!v || !timerRef.current) return;
        const dur = isFinite(v.duration) ? v.duration : 0;
        timerRef.current.textContent = `${fmt(v.currentTime)} / ${fmt(dur)}`;
      }

      // ── Resize handler ──
      const onResize = () => {
        if (!containerRef.current) return;
        const w = containerRef.current.clientWidth;
        const h = containerRef.current.clientHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      };
      window.addEventListener("resize", onResize);

      // ── Animation loop ──
      let animId;
      function animate() {
        animId = requestAnimationFrame(animate);
        getActiveRows().forEach((r) => {
          if (!rows[r].isImage && rows[r]?.playing && !rows[r].video.paused && rows[r].video.readyState >= 2) {
            rows[r].texture.needsUpdate = true;
          }
        });
        renderer.render(scene, camera);
        updateTimer();
      }
      animate();

      // ── Expose state to callbacks ──
      stateRef.current = { rows, pivotGroup, getActiveRows, get rotationY() { return rotationY; }, set rotationY(v) { rotationY = v; }, allPlaying: false };

      // ── Cleanup function ──
      cleanupFn = () => {
        cancelAnimationFrame(animId);
        window.removeEventListener("resize", onResize);
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        renderer.domElement.removeEventListener("pointerdown", onPointerDown);
        renderer.domElement.removeEventListener("touchstart", onTouchStart);
        renderer.domElement.removeEventListener("touchmove", onTouchMove);
        renderer.domElement.removeEventListener("touchend", onTouchEnd);

        // Pause & remove all videos
        discoveredLevels.forEach((lvl) => {
          const row = rows[lvl];
          if (row?.video) { row.video.pause(); row.video.src = ""; row.video.load(); }
          if (row?.texture) row.texture.dispose();
          if (row?.ring) {
            row.ring.geometry.dispose();
            row.ring.material.dispose();
          }
        });
        renderer.dispose();
        if (el.contains(renderer.domElement)) el.removeChild(renderer.domElement);
        stateRef.current = null;
      };
    })();

    return () => {
      cancelled = true;
      if (cleanupFn) cleanupFn();
    };
  }, [ribbonItems]);

  // ── Render ────────────────────────────────────────────────────────────
  const hasVideos = ribbonItems?.some((i) => !isImageFile(i.name));

  return createPortal(
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", top: 0, right: 0, bottom: 0, left: 0,
        zIndex: 99999,
        background: "rgba(0,0,0,0.96)",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        fontFamily: "'Space Grotesk', -apple-system, sans-serif",
      }}
    >
      {/* Close */}
      <button
        onClick={onClose}
        style={{
          position: "absolute", top: 16, right: 20, zIndex: 10,
          background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 8, padding: "8px 14px", color: "#fff", fontSize: 16, cursor: "pointer",
        }}
      >
        ✕
      </button>

      {/* Title bar */}
      <div style={{ marginBottom: 12, textAlign: "center", zIndex: 5 }}>
        <div style={{ color: "#fff", fontSize: 16, fontWeight: 600 }}>
          {asset?.originalName || asset?.fileName || "Tiled Preview"}
        </div>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 4 }}>
          {asset?.lumen8Id && (
            <span style={{ fontSize: 12, fontFamily: "monospace", color: TEAL }}>
              {asset.lumen8Id}
            </span>
          )}
          {asset?.venue && (
            <span style={{ fontSize: 12, color: TEXT_SECONDARY }}>{asset.venue}</span>
          )}
          <span style={{ fontSize: 12, color: TEXT_SECONDARY }}>LED Tiled</span>
          <span style={{ fontSize: 12, color: TEXT_SECONDARY }}>
            {ribbonItems?.length || 0} level{ribbonItems?.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* 3D Viewport */}
      <div
        ref={containerRef}
        style={{
          width: "92vw", maxWidth: 1400, height: "68vh",
          background: "#0a0a0a", borderRadius: 12,
          border: "1px solid #1a1a1a", overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Loading overlay inside viewport */}
        {!ready && (
          <div
            style={{
              position: "absolute", inset: 0, zIndex: 5,
              background: "rgba(10,10,20,0.95)",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
            }}
          >
            <div
              style={{
                width: 40, height: 40,
                border: "3px solid rgba(255,255,255,0.1)",
                borderTopColor: TEAL, borderRadius: "50%",
                animation: "ribbon-spin 0.8s linear infinite",
              }}
            />
            <div style={{ marginTop: 12, fontSize: 13, color: "#888" }}>
              Loading levels… {loadingCount}/{totalCount}
            </div>
            <style>{`@keyframes ribbon-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
      </div>

      {/* Controls */}
      <div
        style={{
          display: "flex", gap: 8, marginTop: 14, alignItems: "center",
          background: "rgba(10,10,20,0.85)", backdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10,
          padding: "10px 18px",
        }}
      >
        {hasVideos && (
          <button
            disabled={!ready}
            onClick={() => { allPlaying ? stopAll() : playAll(); }}
            style={{
              background: allPlaying ? "rgba(239,68,68,0.3)" : "rgba(34,197,94,0.3)",
              border: `1px solid ${allPlaying ? "rgba(239,68,68,0.5)" : "rgba(34,197,94,0.5)"}`,
              color: "#e0e0e0", padding: "8px 16px", borderRadius: 8,
              cursor: ready ? "pointer" : "default", fontSize: 13, fontWeight: 500,
              opacity: ready ? 1 : 0.4,
            }}
          >
            {allPlaying ? "⏹ Stop All" : "▶ Play All"}
          </button>
        )}
        {hasVideos && (
          <span
            ref={timerRef}
            style={{
              fontSize: 13, fontWeight: 500, fontVariantNumeric: "tabular-nums",
              color: "#93c5fd", minWidth: 90, textAlign: "center", letterSpacing: "0.5px",
            }}
          >
            0:00 / 0:00
          </span>
        )}
        <button
          disabled={!ready}
          onClick={resetView}
          style={{
            background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
            color: "#e0e0e0", padding: "8px 16px", borderRadius: 8,
            cursor: ready ? "pointer" : "default", fontSize: 13, fontWeight: 500,
            opacity: ready ? 1 : 0.4,
          }}
        >
          ⟲ Reset
        </button>
      </div>

      {/* Hint */}
      <div style={{ marginTop: 8, fontSize: 11, color: "#555" }}>
        Drag left / right to rotate
      </div>
    </div>,
    document.body
  );
}
